import React, { useState } from 'react';
import { X, Star, MessageSquare, Bug, Lightbulb, LifeBuoy } from 'lucide-react';
import { Button } from '../ui';
import { feedbackService } from '../../services/feedbackService';

interface FeedbackDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

type Category = 'bug' | 'feature' | 'improvement' | 'other';

const categories: { id: Category; label: string; icon: React.ReactNode }[] = [
    { id: 'bug', label: 'Bug Report', icon: <Bug className="w-4 h-4" /> },
    { id: 'feature', label: 'Feature Request', icon: <Lightbulb className="w-4 h-4" /> },
    { id: 'improvement', label: 'Improvement', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'other', label: 'Other', icon: <LifeBuoy className="w-4 h-4" /> },
];

export const FeedbackDrawer: React.FC<FeedbackDrawerProps> = ({ isOpen, onClose }) => {
    const [rating, setRating] = useState<number>(0);
    const [category, setCategory] = useState<Category>('improvement');
    const [comment, setComment] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hoveredRating, setHoveredRating] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);

    const resetForm = () => {
        setRating(0);
        setCategory('improvement');
        setComment('');
        setError(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (rating === 0) return;

        try {
            setIsLoading(true);
            setError(null);

            // Map category labels to exact backend strings
            const categoryMap: Record<Category, string> = {
                bug: 'Bug Report',
                feature: 'Feature Request',
                improvement: 'Improvement',
                other: 'Other'
            };

            const payload = {
                rating,
                category: categoryMap[category],
                details: comment.trim(), // API expects 'details', we use 'comment' in state
            };

            await feedbackService.submitFeedback(payload);
            handleClose();
        } catch (err: any) {
            console.error('Feedback submission error:', err);
            // Error handling is handled by the service (showing toast)
            // But we also show an inline error for better visibility
            setError(err?.response?.data?.message || 'Failed to submit feedback. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Overlay */}
            <div
                className={`fixed inset-0 z-[100] bg-black/20 transition-opacity duration-300 pointer-events-none ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0'
                    }`}
                onClick={handleClose}
            />

            {/* Drawer */}
            <div
                className={`fixed top-0 right-0 bottom-0 z-[101] w-full sm:w-[420px] md:w-[520px] lg:w-[600px] bg-white shadow-2xl transition-transform duration-300 ease-in-out transform border-l border-gray-100 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Send Feedback</h2>
                        <p className="text-xs text-gray-500 mt-0.5">Help us improve your experience</p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {/* Rating */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-gray-700">Experience Rating</label>
                        <div className="flex items-center gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    className="transition-transform active:scale-90"
                                    onMouseEnter={() => setHoveredRating(star)}
                                    onMouseLeave={() => setHoveredRating(0)}
                                    onClick={() => setRating(star)}
                                >
                                    <Star
                                        className={`w-8 h-8 transition-colors ${star <= (hoveredRating || rating)
                                            ? 'fill-yellow-400 text-yellow-400'
                                            : 'text-gray-100'
                                            }`}
                                    />
                                </button>
                            ))}
                        </div>
                        {rating > 0 && (
                            <p className="text-xs font-medium text-blue-600">
                                {rating === 5 ? 'Excellent!' : rating >= 3 ? 'Thanks for the feedback!' : 'We will do better!'}
                            </p>
                        )}
                    </div>

                    {/* Category */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-gray-700">What's on your mind?</label>
                        <div className="flex flex-wrap gap-2">
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    type="button"
                                    onClick={() => setCategory(cat.id)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-medium transition-all ${category === cat.id
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-100 hover:border-gray-200 text-gray-600 bg-white'
                                        }`}
                                >
                                    <span className={category === cat.id ? 'text-blue-500' : 'text-gray-400'}>
                                        {cat.icon}
                                    </span>
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Comment */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-gray-700" htmlFor="feedback-comment">
                            Details
                        </label>
                        <textarea
                            id="feedback-comment"
                            className="w-full min-h-[150px] p-4 rounded-xl border border-gray-200 focus:border-blue-500 outline-none transition-all resize-none text-sm text-gray-700 placeholder:text-gray-400 bg-gray-50/30"
                            placeholder="Tell us more about your experience..."
                            value={comment}
                            onChange={(e) => {
                                setComment(e.target.value);
                                if (error) setError(null);
                            }}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 flex flex-col gap-3 bg-gray-50/30">
                    {error && (
                        <div className="text-xs text-red-600 font-medium text-right px-1">
                            {error}
                        </div>
                    )}
                    <div className="flex items-center justify-end gap-3">
                        <Button variant="ghost" onClick={handleClose} type="button" disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={rating === 0 || isLoading}
                            isLoading={isLoading}
                            size="md"
                            type="submit"
                            className="px-8"
                        >
                            Submit
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};
